import { useRef, useState } from "react";
import { Camera, Loader2, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, imgSrc } from "@/lib/api";

export const compress = (file, max = 1600) =>
  new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      const scale = Math.min(1, max / Math.max(img.width, img.height));
      const canvas = document.createElement("canvas");
      canvas.width = Math.round(img.width * scale);
      canvas.height = Math.round(img.height * scale);
      canvas.getContext("2d").drawImage(img, 0, 0, canvas.width, canvas.height);
      URL.revokeObjectURL(url);
      canvas.toBlob((b) => (b ? resolve(b) : reject(new Error("Could not read image"))), "image/jpeg", 0.86);
    };
    img.onerror = () => { URL.revokeObjectURL(url); reject(new Error("This photo format isn't supported. Try a JPG or PNG.")); };
    img.src = url;
  });

export const uploadPhoto = async (file) => {
  const blob = await compress(file);
  const fd = new FormData();
  fd.append("file", blob, "photo.jpg");
  const { data } = await api.post("/admin/upload", fd);
  return data?.url || "";
};

export const ImageUpload = ({ value, onChange, testId, label = "photo" }) => {
  const input = useRef(null);
  const [busy, setBusy] = useState(false);

  const pick = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setBusy(true);
    try {
      onChange(await uploadPhoto(file));
      toast.success("Photo uploaded");
    } catch (err) {
      toast.error(errMsg(err));
    }
    setBusy(false);
  };

  return (
    <div className="flex items-center gap-4">
      <div className="w-24 h-24 shrink-0 bg-black border border-white/15 overflow-hidden grid place-items-center">
        {value ? <img src={imgSrc(value)} alt="" data-testid={`${testId}-preview`} className="w-full h-full object-cover" /> : <img src="/logo-mark.png" alt="" className="w-14 h-14 object-contain opacity-60" />}
      </div>
      <div className="flex flex-col gap-2">
        <input ref={input} type="file" accept="image/*" onChange={pick} className="hidden" data-testid={`${testId}-input`} />
        <button type="button" data-testid={`${testId}-btn`} disabled={busy} onClick={() => input.current?.click()} className="inline-flex items-center gap-2 bg-[#56ED4A] text-black px-4 py-3 font-mono text-xs uppercase tracking-[0.2em] font-semibold disabled:opacity-60">
          {busy ? <Loader2 size={14} className="animate-spin" /> : <Camera size={14} />} {value ? `Change ${label}` : `Upload ${label}`}
        </button>
        {value && (
          <button type="button" data-testid={`${testId}-remove-btn`} onClick={() => onChange("")} className="inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.2em] text-zinc-400 hover:text-[#FF5A5A]">
            <Trash2 size={12} /> Remove (show logo)
          </button>
        )}
      </div>
    </div>
  );
};
